import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { contentAPI } from '../services/api';
import useNavigationWarning from '../hooks/useNavigationWarning';
import { checkAuthBeforeAction } from '../utils/authHelper';
import NavigationWarningModal from '../components/NavigationWarningModal';
import usePermission from '../hooks/usePermission';
import UpgradePrompt from '../components/UpgradePrompt';

const EmailMarketing = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Permission check
  const { hasModelAccess, getPlanInfo, getRequiredPlanForModel } = usePermission();

  // Upgrade prompt state
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradePromptData, setUpgradePromptData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMode, setLoadingMode] = useState(null); // 'beautify' or 'reply'
  const [generatedContent, setGeneratedContent] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('beautify'); // beautify, reply
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [copiedField, setCopiedField] = useState(null); // 'subject' or 'content'

  const navigationWarning = useNavigationWarning(isLoading, 'An email is being generated. If you leave now, the generation will continue in the background. Are you sure you want to leave?', 'content-generation');

  // Model display mapping - hide technical names
  const getModelDisplayInfo = (modelName) => {
    const modelMap = {
      'deepseek-chat': {
        displayName: 'Basic - Fast Response',
        tier: 'Basic',
        description: 'Quick and efficient for everyday tasks',
        icon: '⚡',
        badge: 'Basic'
      },
      'deepseek-reasoner': {
        displayName: 'Basic - Deep Thinking',
        tier: 'Basic',
        description: 'Advanced reasoning for complex problems',
        icon: '🧠',
        badge: 'Basic'
      },
      'doubao-seed-1-6': {
        displayName: 'Professional - Vision Pro',
        tier: 'Professional',
        description: 'Image understanding + text generation',
        icon: '👁️',
        badge: 'Pro'
      },
      'qwen3-vl-plus': {
        displayName: 'Professional - Vision Plus',
        tier: 'Professional',
        description: 'Advanced multimodal AI with vision',
        icon: '🎯',
        badge: 'Pro'
      }
    };
    return modelMap[modelName] || {
      displayName: modelName,
      tier: 'Standard',
      description: 'AI model',
      icon: '🤖',
      badge: null
    };
  };

  // Form data
  const [formData, setFormData] = useState({
    // Common fields
    language: 'en',

    // Beautify mode fields
    subject: '',
    content: '',
    tone: 'professional',

    // Reply mode fields
    receivedEmail: '',
    replyTone: 'professional',
    keyPoints: '',
  });

  // Language options
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸', popular: true },
    { code: 'zh', name: '中文', flag: '🇨🇳', popular: true },
    { code: 'es', name: 'Español', flag: '🇪🇸', popular: true },
    { code: 'fr', name: 'Français', flag: '🇫🇷', popular: true },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪', popular: true },
    { code: 'ja', name: '日本語', flag: '🇯🇵', popular: true },
    { code: 'ko', name: '한국어', flag: '🇰🇷', popular: true },
    { code: 'pt', name: 'Português', flag: '🇵🇹', popular: true },
    { code: 'ru', name: 'Русский', flag: '🇷🇺', popular: false },
    { code: 'ar', name: 'العربية', flag: '🇸🇦', popular: false },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳', popular: false },
    { code: 'it', name: 'Italiano', flag: '🇮🇹', popular: false },
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱', popular: false },
    { code: 'pl', name: 'Polski', flag: '🇵🇱', popular: false },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷', popular: false },
    { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳', popular: false },
    { code: 'th', name: 'ไทย', flag: '🇹🇭', popular: false },
    { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩', popular: false },
    { code: 'ms', name: 'Bahasa Melayu', flag: '🇲🇾', popular: false },
    { code: 'sv', name: 'Svenska', flag: '🇸🇪', popular: false },
    { code: 'no', name: 'Norsk', flag: '🇳🇴', popular: false },
    { code: 'da', name: 'Dansk', flag: '🇩🇰', popular: false },
    { code: 'fi', name: 'Suomi', flag: '🇫🇮', popular: false },
    { code: 'cs', name: 'Čeština', flag: '🇨🇿', popular: false },
    { code: 'el', name: 'Ελληνικά', flag: '🇬🇷', popular: false },
    { code: 'he', name: 'עברית', flag: '🇮🇱', popular: false },
    { code: 'ro', name: 'Română', flag: '🇷🇴', popular: false },
    { code: 'hu', name: 'Magyar', flag: '🇭🇺', popular: false },
    { code: 'uk', name: 'Українська', flag: '🇺🇦', popular: false },
    { code: 'bg', name: 'Български', flag: '🇧🇬', popular: false },
  ];

  const popularLanguages = languages.filter(lang => lang.popular);

  // Tone icon components
  const ProfessionalIcon = () => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M583.26 622.45a31.61 31.61 0 0 1-14.7-3.65l-58.77-30.9L451 618.8a31.53 31.53 0 0 1-45.74-33.24l11.22-65.44L369 473.77A31.52 31.52 0 0 1 386.42 420l65.71-9.55 29.38-59.54a31.53 31.53 0 0 1 56.55 0l29.38 59.54 65.71 9.55a31.52 31.52 0 0 1 17.47 53.77l-47.55 46.35 11.23 65.44a31.58 31.58 0 0 1-31 36.89zM419.91 460.6L453 492.84a31.5 31.5 0 0 1 9.06 27.9l-7.8 45.52 40.87-21.49a31.52 31.52 0 0 1 29.34 0l40.88 21.49-7.8-45.51a31.51 31.51 0 0 1 9.06-27.91l33.07-32.24L554 454a31.54 31.54 0 0 1-23.74-17.24l-20.44-41.41-20.43 41.41A31.54 31.54 0 0 1 465.61 454z m150.67-43.8z" fill="currentColor" />
      <path d="M509.81 834.37h-0.05a23.19 23.19 0 0 1-5.6-0.68c-45.92-9.24-88.33-26.69-126.06-51.87a372.6 372.6 0 0 1-124-138.18c-0.75-1.46-1.5-2.91-2.23-4.38a303.12 303.12 0 0 1-32.06-135.34V295.76a64.37 64.37 0 0 1 59.6-64c56-4 108.36-16.82 155.52-38.19 36.19-16.39 56.53-32.17 60.24-35.17a22.5 22.5 0 0 1 14.14-5h0.87a22.5 22.5 0 0 1 14.14 5c3.71 3 24 18.78 60.24 35.17 47.16 21.37 99.49 34.21 155.52 38.18a64.37 64.37 0 0 1 59.6 64v208.17a303.19 303.19 0 0 1-32.06 135.35c-0.73 1.46-1.48 2.92-2.23 4.37a372.6 372.6 0 0 1-124 138.18c-37.73 25.18-80.14 42.63-126.06 51.87a23.19 23.19 0 0 1-5.52 0.68z m0-45h-0.78 1.55z m0-585.93a378.47 378.47 0 0 1-56.25 31.12c-52 23.56-109.54 37.72-170.9 42.07a19.23 19.23 0 0 0-17.79 19.13v208.16a258 258 0 0 0 27.28 115.16c0.65 1.3 1.31 2.6 2 3.89a327.49 327.49 0 0 0 109 121.42 304.82 304.82 0 0 0 106.64 44.51 304.85 304.85 0 0 0 106.7-44.51A327.44 327.44 0 0 0 725.46 623c0.67-1.29 1.33-2.58 2-3.88a258 258 0 0 0 27.28-115.16v-208.2a19.23 19.23 0 0 0-17.79-19.13c-61.36-4.35-118.86-18.51-170.9-42.07a377.73 377.73 0 0 1-56.26-31.12z" fill="currentColor" />
    </svg>
  );

  const FriendlyIcon = () => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M512.649853 0C230.403799 0 0.767826 229.655969 0.767826 511.882027c0 282.229057 229.655969 511.886026 511.882027 511.886026 282.229057 0 511.886026-229.656968 511.886026-511.886026C1024.534879 229.655969 794.898906 0 512.649853 0z m0 981.346664c-258.862351 0-469.439643-210.599286-469.439643-469.464637 0-258.860352 210.577291-469.464637 469.439643-469.464637 258.865351 0 469.444641 210.604285 469.444641 469.464637-0.001 258.865351-210.579291 469.464637-469.444641 469.464637z m0-474.302541" fill="currentColor" />
      <path d="M377.443485 461.147521a17.113123 17.113123 0 0 1-13.720891-6.922431c-13.547931-18.167884-30.770029-27.654734-49.83271-27.654735a66.630904 66.630904 0 0 0-52.525099 27.864687c-5.641722 7.579283-16.358294 9.146928-23.934578 3.505206-7.579283-5.641722-9.146928-16.358294-3.505206-23.933578a100.955127 100.955127 0 0 1 79.922893-41.626569c29.918222 0 57.312015 14.82864 77.230502 41.626569a17.096127 17.096127 0 1 1-13.719891 27.267822l0.08498-0.126971z m269.301987 0a17.095127 17.095127 0 0 1-12.822095-28.505541 108.037523 108.037523 0 0 1 78.16829-35.988847c28.677503 0 55.175499 12.097259 76.93157 35.645924 6.419546 6.926431 6.011638 17.746979-0.918792 24.167525-6.926431 6.419546-17.747979 6.011638-24.167524-0.918792-15.089581-16.15634-32.311679-24.704403-51.032438-24.704403h-0.555874a74.626093 74.626093 0 0 0-52.868023 24.490451 17.095127 17.095127 0 0 1-12.735114 5.813683zM510.149419 644.541971h-4.700935c-66.245991-2.392458-111.121824-55.346461-112.791445-57.568957-6.042631-7.260355-5.057854-18.042912 2.202501-24.085543 7.256356-6.043631 18.039913-5.054855 24.082543 2.201501 0.513884 0.599864 37.311547 43.596123 88.044053 45.304736 31.969757 1.065759 63.680572-14.447727 94.027697-46.203532a17.101126 17.101126 0 0 1 16.517258-4.972874 17.088128 17.088128 0 0 1 12.440181 11.945294 17.089128 17.089128 0 0 1-4.296027 16.705215c-36.160807 37.397527-75.009006 56.67416-115.525826 56.67416z m0 0" fill="currentColor" />
    </svg>
  );

  const CasualIcon = () => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M965.40416 342.76608c-19.50976-52.24704-47.744-100.41472-83.92064-143.16288-18.26688-21.5872-50.57536-24.27392-72.15872-6.0096-21.58464 18.26688-24.2752 50.5728-6.00832 72.15872C861.4336 334.42816 893.44 421.88032 893.44 512c0 210.32576-171.11424 381.44-381.44 381.44-210.32704 0-381.44-171.11424-381.44-381.44 0-210.32704 171.11296-381.44 381.44-381.44 60.48896 0 118.30784 13.72672 171.85152 40.8 25.2288 12.75648 56.03328 2.64704 68.7936-22.58816 12.76032-25.23392 2.64576-56.03456-22.58816-68.7936C662.05312 45.5936 588.68992 28.16 512 28.16c-65.29536 0-128.66432 12.8-188.34688 38.04416-57.62176 24.37248-109.36064 59.2512-153.7792 103.66976-44.41856 44.41856-79.29856 96.15744-103.66976 153.77792C40.96 383.3344 28.16 446.70464 28.16 512c0 65.29536 12.8 128.6656 38.04416 188.34688 24.37248 57.62176 59.2512 109.36064 103.66976 153.7792s96.15744 79.29856 153.7792 103.66976C383.33568 983.04 446.70464 995.84 512 995.84c65.29536 0 128.6656-12.8 188.34816-38.04416 57.62176-24.3712 109.36064-59.2512 153.77792-103.66976 44.41856-44.41856 79.29856-96.15744 103.66976-153.7792C983.04 640.6656 995.84 577.29536 995.84 512 995.84 453.79328 985.6 396.85504 965.40416 342.76608z" fill="currentColor" />
      <path d="M274.8864 286.7584c-23.98464 0-16.78336 26.016-6.09536 35.84128L483.552 520.1152l0 181.71904c-87.47136 3.37664-153.17376 21.76896-153.17376 44.08704 0 24.60032 79.7952 24.60032 181.12 24.60032 101.40544 0 180.96384 0 180.96384-24.60032 0-22.39488-65.23136-40.86528-153.02272-44.08704L539.43936 519.87584l214.4448-197.27616 0.00512 0c10.68544-9.82656 18.84288-35.68 0-35.68L274.8864 286.7584z" fill="currentColor" />
    </svg>
  );

  const EnthusiasticIcon = () => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M281.6 480c32 19.2 44.8 38.4 44.8 64v243.2c0 51.2-6.4 57.6-64 64 0-6.4-6.4-19.2-6.4-25.6v-256c6.4-32 19.2-57.6 25.6-89.6zM339.2 819.2V531.2c32 32 70.4 51.2 64 96-6.4 38.4 0 83.2 0 128 0 51.2-6.4 51.2-64 64zM185.6 876.8c-19.2-96 0-185.6 57.6-281.6 0 19.2 6.4 25.6 6.4 38.4v185.6c0 44.8-12.8 57.6-64 57.6zM697.6 275.2c44.8 25.6 44.8 32 12.8 64-70.4 64-140.8 121.6-211.2 185.6-6.4 6.4-12.8 12.8-25.6 19.2l-6.4-6.4C544 448 620.8 364.8 697.6 275.2zM416 608c57.6 38.4 83.2 96 57.6 147.2-6.4 19.2-32 25.6-57.6 38.4V608zM556.8 608L768 512c19.2-6.4 25.6 0 25.6 12.8s0 32-6.4 32c-76.8 25.6-147.2 44.8-224 57.6 0 6.4 0 0-6.4-6.4zM416 480c0-25.6 6.4-44.8 6.4-70.4 6.4-44.8 12.8-96 19.2-140.8 0-25.6 12.8-25.6 32-19.2 19.2 0 25.6 6.4 19.2 25.6-25.6 70.4-44.8 140.8-64 204.8h-12.8zM768 166.4c25.6 6.4 44.8 6.4 70.4 6.4 0 19.2 6.4 44.8 12.8 70.4-19.2 6.4-38.4 19.2-64 32-12.8-19.2-32-32-51.2-51.2 6.4-12.8 19.2-38.4 32-57.6zM793.6 390.4c12.8-12.8 25.6-19.2 38.4-32 12.8 6.4 25.6 19.2 44.8 25.6-6.4 12.8-6.4 32-12.8 51.2-12.8 0-32 0-51.2 6.4-6.4-19.2-12.8-38.4-19.2-51.2zM627.2 153.6c-12.8 12.8-25.6 19.2-32 32l6.4 6.4c-12.8-6.4-32-12.8-44.8-19.2 0-12.8 6.4-25.6 6.4-44.8 12.8 0 32-6.4 44.8-6.4 6.4 12.8 12.8 25.6 19.2 32z" fill="currentColor" />
    </svg>
  );

  const EmpatheticIcon = () => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M512 64C264.58 64 64 264.58 64 512s200.58 448 448 448 448-200.58 448-448S759.42 64 512 64z m229.39 688.76H282.61v-81.22h458.78z m0-200.15H282.61v-81.22h458.78z m0-200.15H282.61v-81.22h458.78z" fill="currentColor" />
    </svg>
  );

  const FormalIcon = () => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M512 53.248C258.6112 53.248 53.248 258.6112 53.248 512s205.3632 458.752 458.752 458.752 458.752-205.3632 458.752-458.752S765.3888 53.248 512 53.248z m217.997312 419.5072l-87.4496 85.2992c-8.423424 8.153088-12.275712 19.980288-10.215424 31.5392l20.698112 120.4224c1.522688 9.1392-7.974912 16.128-16.217088 11.737088l-108.147712-56.895488a35.56352 35.56352 0 0 0-33.152 0l-108.2368 56.896512c-8.153088 4.3008-17.7408-2.688-16.217088-11.738112l20.608-120.4224c1.9712-11.558912-1.882112-23.385088-10.215424-31.5392l-87.4496-85.2992c-6.6304-6.4512-2.956288-17.7408 6.182912-19.084288l120.870912-17.5616c11.648-1.702912 21.6832-8.96 26.88-19.5328L501.9648 306.9952c4.1216-8.333312 15.9488-8.333312 20.0704 0l54.028288 109.580288c5.197824 10.483712 15.232 17.830912 26.88 19.5328l120.870912 17.5616c9.228288 1.344512 12.813312 12.634112 6.182912 19.085312z" fill="currentColor" />
    </svg>
  );

  // Tone options
  const toneOptions = [
    { value: 'professional', label: 'Professional', icon: <ProfessionalIcon />, desc: 'Formal and business-appropriate' },
    { value: 'friendly', label: 'Friendly', icon: <FriendlyIcon />, desc: 'Warm and approachable' },
    { value: 'casual', label: 'Casual', icon: <CasualIcon />, desc: 'Relaxed and conversational' },
    { value: 'enthusiastic', label: 'Enthusiastic', icon: <EnthusiasticIcon />, desc: 'Energetic and positive' },
    { value: 'empathetic', label: 'Empathetic', icon: <EmpatheticIcon />, desc: 'Understanding and supportive' },
    { value: 'formal', label: 'Formal', icon: <FormalIcon />, desc: 'Very professional and respectful' },
  ];

  // Load available AI models
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await contentAPI.getModels();
        console.log('Models API response:', response.data);
        const models = response.data.data || response.data;
        if (models && models.length > 0) {
          setAvailableModels(models);
          setSelectedModel(models[0]);
        }
      } catch (err) {
        console.error('Failed to fetch models:', err);
      }
    };
    fetchModels();
  }, []);

  // Handle prefill data from Workspace
  useEffect(() => {
    if (location.state?.prefillData) {
      const prefill = location.state.prefillData;
      const newFormData = {};

      // Set mode if provided
      if (location.state.mode) {
        setMode(location.state.mode);
      }

      // Copy all prefill data to form
      Object.keys(prefill).forEach(key => {
        if (prefill[key] !== undefined && prefill[key] !== '') {
          newFormData[key] = prefill[key];
        }
      });

      if (Object.keys(newFormData).length > 0) {
        setFormData(prev => ({
          ...prev,
          ...newFormData
        }));
      }

      // If there's generated content, display it
      if (prefill.generatedSubject || prefill.generatedContent) {
        setGeneratedContent({
          subject: prefill.generatedSubject,
          content: prefill.generatedContent
        });
      }
    }
  }, [location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBeautifyEmail = async () => {
    // Check authentication before making API call
    if (!checkAuthBeforeAction(navigate)) {
      return;
    }

    // Check model permission before generating
    if (selectedModel && !hasModelAccess(selectedModel.name)) {
      const modelInfo = getModelDisplayInfo(selectedModel.name);
      const planInfo = getPlanInfo();
      setUpgradePromptData({
        type: 'model',
        featureName: modelInfo.displayName,
        currentPlan: planInfo.name,
        requiredPlan: getRequiredPlanForModel(selectedModel.name)
      });
      setShowUpgradePrompt(true);
      return;
    }

    setIsLoading(true);
    setLoadingMode('beautify');
    setError(null);
    setGeneratedContent(null);

    try {
      // Validate required fields
      if (!formData.subject || !formData.content) {
        throw new Error('Please provide both subject and content');
      }

      if (!selectedModel) {
        throw new Error('Please select an AI model');
      }

      const response = await contentAPI.beautifyEmail({
        subject: formData.subject,
        content: formData.content,
        tone: formData.tone,
        language: formData.language,
        modelId: selectedModel.id
      });

      if (response.data.success) {
        setGeneratedContent(response.data.data);
      } else {
        throw new Error(response.data.error?.message || 'Failed to beautify email');
      }
    } catch (err) {
      console.error('Email beautify error:', err);
      setError(err.response?.data?.error?.message || err.message || 'Failed to beautify email');
    } finally {
      setIsLoading(false);
      setLoadingMode(null);
    }
  };

  const handleReplyEmail = async () => {
    // Check authentication before making API call
    if (!checkAuthBeforeAction(navigate)) {
      return;
    }

    // Check model permission before generating
    if (selectedModel && !hasModelAccess(selectedModel.name)) {
      const modelInfo = getModelDisplayInfo(selectedModel.name);
      const planInfo = getPlanInfo();
      setUpgradePromptData({
        type: 'model',
        featureName: modelInfo.displayName,
        currentPlan: planInfo.name,
        requiredPlan: getRequiredPlanForModel(selectedModel.name)
      });
      setShowUpgradePrompt(true);
      return;
    }

    setIsLoading(true);
    setLoadingMode('reply');
    setError(null);
    setGeneratedContent(null);

    try {
      // Validate required fields
      if (!formData.receivedEmail) {
        throw new Error('Please provide the received email');
      }

      if (!selectedModel) {
        throw new Error('Please select an AI model');
      }

      const response = await contentAPI.replyToEmail({
        receivedEmail: formData.receivedEmail,
        tone: formData.replyTone,
        keyPoints: formData.keyPoints,
        language: formData.language,
        modelId: selectedModel.id
      });

      if (response.data.success) {
        setGeneratedContent(response.data.data);
      } else {
        throw new Error(response.data.error?.message || 'Failed to generate reply');
      }
    } catch (err) {
      console.error('Email reply error:', err);
      setError(err.response?.data?.error?.message || err.message || 'Failed to generate reply');
    } finally {
      setIsLoading(false);
      setLoadingMode(null);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    // Reset after 2 seconds
    setTimeout(() => {
      setCopiedField(null);
    }, 2000);
  };

  return (
    <>
      <NavigationWarningModal
        isOpen={navigationWarning.showModal}
        onCancel={navigationWarning.handleCancelLeave}
        onConfirm={navigationWarning.handleConfirmLeave}
        message={navigationWarning.warningMessage}
        taskType={navigationWarning.taskType}
      />

      <div className="flex h-screen bg-gray-50">
        {/* Sidebar */}
        <Sidebar />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-4">
                <span className="text-5xl">✉️</span>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    Email Writing Assistant
                  </h1>
                  <p className="text-gray-600 mt-1">
                    AI-powered email composition and reply assistant
                  </p>
                </div>
              </div>

              {/* Model Selector */}
              <div className="flex items-center space-x-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <span className="text-sm text-blue-900 font-medium">AI Model:</span>
                <select
                  value={selectedModel?.id || ''}
                  onChange={(e) => {
                    const model = availableModels.find(m => m.id === e.target.value);
                    setSelectedModel(model);
                  }}
                  className="flex-1 max-w-xs px-3 py-2 pr-8 border border-blue-300 bg-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-medium hover:bg-gray-50 transition cursor-pointer appearance-none bg-no-repeat bg-right"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundSize: '1.25rem',
                    backgroundPosition: 'right 0.5rem center'
                  }}
                >
                  {availableModels.length === 0 ? (
                    <option value="">Loading models...</option>
                  ) : (
                    availableModels.map((model) => {
                      const modelInfo = getModelDisplayInfo(model.name);
                      const hasAccess = hasModelAccess(model.name);
                      return (
                        <option key={model.id} value={model.id} disabled={!hasAccess}>
                          {modelInfo.icon} {modelInfo.displayName} {!hasAccess ? '🔒' : ''}
                        </option>
                      );
                    })
                  )}
                </select>

                {/* Model Badge */}
                {selectedModel && (
                  <div className="flex items-center">
                    {(() => {
                      const modelInfo = getModelDisplayInfo(selectedModel.name);
                      const badgeColors = {
                        'Basic': 'bg-gray-100 text-gray-700 border-gray-300',
                        'Pro': 'bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 border-purple-300'
                      };
                      if (!modelInfo.badge) return null;
                      return (
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${badgeColors[modelInfo.badge] || 'bg-gray-100 text-gray-700 border-gray-300'}`}>
                          {modelInfo.badge}
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>

            {/* Mode Selection */}
            <div className="mb-6">
              <div className="bg-white rounded-lg shadow-sm p-2 inline-flex">
                <button
                  onClick={() => setMode('beautify')}
                  className={`px-6 py-3 rounded-md font-medium transition flex items-center ${
                    mode === 'beautify'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <svg className="mr-2 w-5 h-5" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                    <path d="M951.9 450.2l-98.1-131.5 52.7-155.4c4.4-13 1.1-27.3-8.6-37s-24-13-37-8.6l-155.4 52.7L574 72.3c-11-8.2-25.7-9.4-37.9-3.2s-19.8 18.8-19.7 32.5l2.1 164-133.9 94.7c-11.2 7.9-16.9 21.5-14.8 35 2.1 13.5 11.8 24.7 24.9 28.7l117.8 36.6L74.6 897.8c-14.1 14-14.1 36.8 0 50.9 7 7 16.3 10.6 25.5 10.6s18.4-3.5 25.4-10.5l437.9-437.1L600 629.5c4.1 13.1 15.2 22.7 28.7 24.9 1.9 0.3 3.8 0.4 5.6 0.4 11.6 0 22.6-5.6 29.4-15.2l94.7-133.9 164 2.1c13.7 0.2 26.3-7.4 32.5-19.7 6.5-12.2 5.2-26.9-3-37.9z m-211.4-16.8c-11.8-0.1-23 5.5-29.9 15.2l-63.5 89.8-32.7-105.1c-3.5-11.3-12.4-20.2-23.7-23.7l-105-32.6 89.8-63.5c9.7-6.8 15.4-18 15.2-29.9l-1.4-110 88.2 65.8c9.5 7.1 21.9 9 33.1 5.2l104.2-35.3-35.3 104.2c-3.8 11.2-1.8 23.6 5.2 33.1l65.8 88.2-110-1.4z" fill="currentColor" />
                  </svg>
                  Beautify Email
                </button>
                <button
                  onClick={() => setMode('reply')}
                  className={`px-6 py-3 rounded-md font-medium transition flex items-center ${
                    mode === 'reply'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <svg className="mr-2 w-5 h-5" viewBox="0 0 1026 1024" xmlns="http://www.w3.org/2000/svg">
                    <path d="M654.222222 631.466667h176.355556c68.266667 0 136.533333 39.822222 170.666666 96.711111 34.133333 62.577778 34.133333 136.533333 0 199.111111s-102.4 96.711111-170.666666 96.711111h-79.644445c-11.377778 0-28.444444-5.688889-34.133333-17.066667-5.688889-11.377778-5.688889-28.444444 0-39.822222 5.688889-11.377778 22.755556-17.066667 34.133333-17.066667h79.644445c62.577778 0 119.466667-51.2 119.466666-119.466666 0-62.577778-51.2-119.466667-119.466666-119.466667h-176.355556l45.511111 45.511111c17.066667 17.066667 17.066667 39.822222 0 56.888889-17.066667 17.066667-39.822222 17.066667-56.888889 0l-113.777777-113.777778c-5.688889-5.688889-11.377778-17.066667-11.377778-28.444444s5.688889-22.755556 11.377778-28.444445l113.777777-113.777777c11.377778-11.377778 22.755556-11.377778 39.822223-11.377778 11.377778 5.688889 22.755556 17.066667 28.444444 28.444444 5.688889 11.377778 0 28.444444-11.377778 39.822223l-45.511111 45.511111z m-102.4 312.888889c11.377778 0 28.444444 5.688889 34.133334 17.066666 5.688889 11.377778 5.688889 28.444444 0 39.822222-5.688889 17.066667-22.755556 22.755556-34.133334 22.755556H119.466667C51.2 1024 0 972.8 0 904.533333V119.466667C0 51.2 51.2 0 119.466667 0h711.111111c62.577778 0 119.466667 51.2 119.466666 119.466667V512c0 11.377778-5.688889 28.444444-17.066666 34.133333-11.377778 5.688889-28.444444 5.688889-39.822222 0-11.377778-5.688889-17.066667-22.755556-17.066667-34.133333V119.466667c0-22.755556-17.066667-39.822222-39.822222-39.822223H119.466667c-22.755556 0-39.822222 17.066667-39.822223 39.822223v785.066666c0 22.755556 17.066667 39.822222 39.822223 39.822223h432.355555zM238.933333 199.111111h472.177778c22.755556 0 39.822222 17.066667 39.822222 39.822222 0 22.755556-17.066667 39.822222-39.822222 39.822223H238.933333c-22.755556 0-39.822222-17.066667-39.822222-39.822223 0-22.755556 17.066667-39.822222 39.822222-39.822222z m0 233.244445h238.933334c17.066667 0 34.133333 17.066667 34.133333 39.822222s-17.066667 39.822222-39.822222 39.822222H238.933333c-22.755556 0-39.822222-17.066667-39.822222-39.822222s17.066667-39.822222 39.822222-39.822222z m0 0" fill="currentColor" />
                  </svg>
                  Reply to Email
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <svg className="h-5 w-5 text-red-400 mr-3" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <p className="mt-1 text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Input */}
              <div className="space-y-6">
                {/* Language Selection */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Language</h2>

                  {/* Popular Languages - Quick Select */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Popular Languages</label>
                    <div className="grid grid-cols-4 gap-2">
                      {popularLanguages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => setFormData(prev => ({ ...prev, language: lang.code }))}
                          className={`p-2 rounded-lg border-2 text-center transition ${
                            formData.language === lang.code
                              ? 'border-primary-500 bg-primary-50 shadow-sm'
                              : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="text-xl mb-1">{lang.flag}</div>
                          <div className="text-xs font-medium text-gray-900">{lang.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* All Languages - Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">All Languages</label>
                    <select
                      value={formData.language}
                      onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      {languages.map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.flag} {lang.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Beautify Mode */}
                {mode === 'beautify' && (
                  <>
                    <div className="bg-white rounded-lg shadow-sm p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Content</h2>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Subject *
                          </label>
                          <input
                            type="text"
                            name="subject"
                            value={formData.subject}
                            onChange={handleInputChange}
                            placeholder="Enter email subject"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Content *
                          </label>
                          <textarea
                            name="content"
                            value={formData.content}
                            onChange={handleInputChange}
                            placeholder="Enter your email content here. The AI will beautify and polish it for you."
                            rows={10}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            {formData.content.length} characters
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">Tone</h2>
                      <div className="grid grid-cols-2 gap-3">
                        {toneOptions.map((tone) => (
                          <button
                            key={tone.value}
                            onClick={() => setFormData(prev => ({ ...prev, tone: tone.value }))}
                            className={`p-4 rounded-lg border-2 text-left transition ${
                              formData.tone === tone.value
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center mb-1">
                              <span className="text-2xl mr-2">{tone.icon}</span>
                              <span className="text-sm font-medium text-gray-900">{tone.label}</span>
                            </div>
                            <div className="text-xs text-gray-500">{tone.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={handleBeautifyEmail}
                      disabled={isLoading || !formData.subject || !formData.content}
                      className="w-full bg-primary-600 text-white px-6 py-4 rounded-lg font-medium hover:bg-primary-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {loadingMode === 'beautify' ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Beautifying Email...
                        </span>
                      ) : loadingMode === 'reply' ? (
                        <span className="flex items-center justify-center text-gray-400">
                          <svg className="mr-2 w-5 h-5" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                            <path d="M951.9 450.2l-98.1-131.5 52.7-155.4c4.4-13 1.1-27.3-8.6-37s-24-13-37-8.6l-155.4 52.7L574 72.3c-11-8.2-25.7-9.4-37.9-3.2s-19.8 18.8-19.7 32.5l2.1 164-133.9 94.7c-11.2 7.9-16.9 21.5-14.8 35 2.1 13.5 11.8 24.7 24.9 28.7l117.8 36.6L74.6 897.8c-14.1 14-14.1 36.8 0 50.9 7 7 16.3 10.6 25.5 10.6s18.4-3.5 25.4-10.5l437.9-437.1L600 629.5c4.1 13.1 15.2 22.7 28.7 24.9 1.9 0.3 3.8 0.4 5.6 0.4 11.6 0 22.6-5.6 29.4-15.2l94.7-133.9 164 2.1c13.7 0.2 26.3-7.4 32.5-19.7 6.5-12.2 5.2-26.9-3-37.9z m-211.4-16.8c-11.8-0.1-23 5.5-29.9 15.2l-63.5 89.8-32.7-105.1c-3.5-11.3-12.4-20.2-23.7-23.7l-105-32.6 89.8-63.5c9.7-6.8 15.4-18 15.2-29.9l-1.4-110 88.2 65.8c9.5 7.1 21.9 9 33.1 5.2l104.2-35.3-35.3 104.2c-3.8 11.2-1.8 23.6 5.2 33.1l65.8 88.2-110-1.4z" fill="currentColor" />
                          </svg>
                          Replying to Email...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center">
                          <svg className="mr-2 w-5 h-5" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                            <path d="M951.9 450.2l-98.1-131.5 52.7-155.4c4.4-13 1.1-27.3-8.6-37s-24-13-37-8.6l-155.4 52.7L574 72.3c-11-8.2-25.7-9.4-37.9-3.2s-19.8 18.8-19.7 32.5l2.1 164-133.9 94.7c-11.2 7.9-16.9 21.5-14.8 35 2.1 13.5 11.8 24.7 24.9 28.7l117.8 36.6L74.6 897.8c-14.1 14-14.1 36.8 0 50.9 7 7 16.3 10.6 25.5 10.6s18.4-3.5 25.4-10.5l437.9-437.1L600 629.5c4.1 13.1 15.2 22.7 28.7 24.9 1.9 0.3 3.8 0.4 5.6 0.4 11.6 0 22.6-5.6 29.4-15.2l94.7-133.9 164 2.1c13.7 0.2 26.3-7.4 32.5-19.7 6.5-12.2 5.2-26.9-3-37.9z m-211.4-16.8c-11.8-0.1-23 5.5-29.9 15.2l-63.5 89.8-32.7-105.1c-3.5-11.3-12.4-20.2-23.7-23.7l-105-32.6 89.8-63.5c9.7-6.8 15.4-18 15.2-29.9l-1.4-110 88.2 65.8c9.5 7.1 21.9 9 33.1 5.2l104.2-35.3-35.3 104.2c-3.8 11.2-1.8 23.6 5.2 33.1l65.8 88.2-110-1.4z" fill="currentColor" />
                          </svg>
                          Beautify Email
                        </span>
                      )}
                    </button>
                  </>
                )}

                {/* Reply Mode */}
                {mode === 'reply' && (
                  <>
                    <div className="bg-white rounded-lg shadow-sm p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">Received Email</h2>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Paste the email you received *
                          </label>
                          <textarea
                            name="receivedEmail"
                            value={formData.receivedEmail}
                            onChange={handleInputChange}
                            placeholder="Paste the email content you want to reply to..."
                            rows={12}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            {formData.receivedEmail.length} characters
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Key Points to Include (Optional)
                          </label>
                          <textarea
                            name="keyPoints"
                            value={formData.keyPoints}
                            onChange={handleInputChange}
                            placeholder="List any specific points you want to address in your reply..."
                            rows={4}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">Reply Tone</h2>
                      <div className="grid grid-cols-2 gap-3">
                        {toneOptions.map((tone) => (
                          <button
                            key={tone.value}
                            onClick={() => setFormData(prev => ({ ...prev, replyTone: tone.value }))}
                            className={`p-4 rounded-lg border-2 text-left transition ${
                              formData.replyTone === tone.value
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center mb-1">
                              <span className="text-2xl mr-2">{tone.icon}</span>
                              <span className="text-sm font-medium text-gray-900">{tone.label}</span>
                            </div>
                            <div className="text-xs text-gray-500">{tone.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={handleReplyEmail}
                      disabled={isLoading || !formData.receivedEmail}
                      className="w-full bg-primary-600 text-white px-6 py-4 rounded-lg font-medium hover:bg-primary-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {loadingMode === 'reply' ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Generating Reply...
                        </span>
                      ) : loadingMode === 'beautify' ? (
                        <span className="flex items-center justify-center text-gray-400">
                          <svg className="mr-2 w-5 h-5" viewBox="0 0 1026 1024" xmlns="http://www.w3.org/2000/svg">
                            <path d="M654.222222 631.466667h176.355556c68.266667 0 136.533333 39.822222 170.666666 96.711111 34.133333 62.577778 34.133333 136.533333 0 199.111111s-102.4 96.711111-170.666666 96.711111h-79.644445c-11.377778 0-28.444444-5.688889-34.133333-17.066667-5.688889-11.377778-5.688889-28.444444 0-39.822222 5.688889-11.377778 22.755556-17.066667 34.133333-17.066667h79.644445c62.577778 0 119.466667-51.2 119.466666-119.466666 0-62.577778-51.2-119.466667-119.466666-119.466667h-176.355556l45.511111 45.511111c17.066667 17.066667 17.066667 39.822222 0 56.888889-17.066667 17.066667-39.822222 17.066667-56.888889 0l-113.777777-113.777778c-5.688889-5.688889-11.377778-17.066667-11.377778-28.444444s5.688889-22.755556 11.377778-28.444445l113.777777-113.777777c11.377778-11.377778 22.755556-11.377778 39.822223-11.377778 11.377778 5.688889 22.755556 17.066667 28.444444 28.444444 5.688889 11.377778 0 28.444444-11.377778 39.822223l-45.511111 45.511111z m-102.4 312.888889c11.377778 0 28.444444 5.688889 34.133334 17.066666 5.688889 11.377778 5.688889 28.444444 0 39.822222-5.688889 17.066667-22.755556 22.755556-34.133334 22.755556H119.466667C51.2 1024 0 972.8 0 904.533333V119.466667C0 51.2 51.2 0 119.466667 0h711.111111c62.577778 0 119.466667 51.2 119.466666 119.466667V512c0 11.377778-5.688889 28.444444-17.066666 34.133333-11.377778 5.688889-28.444444 5.688889-39.822222 0-11.377778-5.688889-17.066667-22.755556-17.066667-34.133333V119.466667c0-22.755556-17.066667-39.822222-39.822222-39.822223H119.466667c-22.755556 0-39.822222 17.066667-39.822223 39.822223v785.066666c0 22.755556 17.066667 39.822222 39.822223 39.822223h432.355555zM238.933333 199.111111h472.177778c22.755556 0 39.822222 17.066667 39.822222 39.822222 0 22.755556-17.066667 39.822222-39.822222 39.822223H238.933333c-22.755556 0-39.822222-17.066667-39.822222-39.822223 0-22.755556 17.066667-39.822222 39.822222-39.822222z m0 233.244445h238.933334c17.066667 0 34.133333 17.066667 34.133333 39.822222s-17.066667 39.822222-39.822222 39.822222H238.933333c-22.755556 0-39.822222-17.066667-39.822222-39.822222s17.066667-39.822222 39.822222-39.822222z m0 0" fill="currentColor" />
                          </svg>
                          Beautifying Email...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center">
                          <svg className="mr-2 w-5 h-5" viewBox="0 0 1026 1024" xmlns="http://www.w3.org/2000/svg">
                            <path d="M654.222222 631.466667h176.355556c68.266667 0 136.533333 39.822222 170.666666 96.711111 34.133333 62.577778 34.133333 136.533333 0 199.111111s-102.4 96.711111-170.666666 96.711111h-79.644445c-11.377778 0-28.444444-5.688889-34.133333-17.066667-5.688889-11.377778-5.688889-28.444444 0-39.822222 5.688889-11.377778 22.755556-17.066667 34.133333-17.066667h79.644445c62.577778 0 119.466667-51.2 119.466666-119.466666 0-62.577778-51.2-119.466667-119.466666-119.466667h-176.355556l45.511111 45.511111c17.066667 17.066667 17.066667 39.822222 0 56.888889-17.066667 17.066667-39.822222 17.066667-56.888889 0l-113.777777-113.777778c-5.688889-5.688889-11.377778-17.066667-11.377778-28.444444s5.688889-22.755556 11.377778-28.444445l113.777777-113.777777c11.377778-11.377778 22.755556-11.377778 39.822223-11.377778 11.377778 5.688889 22.755556 17.066667 28.444444 28.444444 5.688889 11.377778 0 28.444444-11.377778 39.822223l-45.511111 45.511111z m-102.4 312.888889c11.377778 0 28.444444 5.688889 34.133334 17.066666 5.688889 11.377778 5.688889 28.444444 0 39.822222-5.688889 17.066667-22.755556 22.755556-34.133334 22.755556H119.466667C51.2 1024 0 972.8 0 904.533333V119.466667C0 51.2 51.2 0 119.466667 0h711.111111c62.577778 0 119.466667 51.2 119.466666 119.466667V512c0 11.377778-5.688889 28.444444-17.066666 34.133333-11.377778 5.688889-28.444444 5.688889-39.822222 0-11.377778-5.688889-17.066667-22.755556-17.066667-34.133333V119.466667c0-22.755556-17.066667-39.822222-39.822222-39.822223H119.466667c-22.755556 0-39.822222 17.066667-39.822223 39.822223v785.066666c0 22.755556 17.066667 39.822222 39.822223 39.822223h432.355555zM238.933333 199.111111h472.177778c22.755556 0 39.822222 17.066667 39.822222 39.822222 0 22.755556-17.066667 39.822222-39.822222 39.822223H238.933333c-22.755556 0-39.822222-17.066667-39.822222-39.822223 0-22.755556 17.066667-39.822222 39.822222-39.822222z m0 233.244445h238.933334c17.066667 0 34.133333 17.066667 34.133333 39.822222s-17.066667 39.822222-39.822222 39.822222H238.933333c-22.755556 0-39.822222-17.066667-39.822222-39.822222s17.066667-39.822222 39.822222-39.822222z m0 0" fill="currentColor" />
                          </svg>
                          Reply to Email
                        </span>
                      )}
                    </button>
                  </>
                )}
              </div>

              {/* Right Column - Output */}
              <div className="space-y-6">
                {generatedContent ? (
                  <>
                    {/* Subject */}
                    {generatedContent.subject && (
                      <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-gray-900">Subject</h3>
                          <button
                            onClick={() => copyToClipboard(generatedContent.subject, 'subject')}
                            className={`text-sm flex items-center transition-all ${
                              copiedField === 'subject'
                                ? 'text-green-600'
                                : 'text-primary-600 hover:text-primary-700'
                            }`}
                          >
                            {copiedField === 'subject' ? (
                              <>
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Copied!
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Copy
                              </>
                            )}
                          </button>
                        </div>
                        <p className="text-lg font-medium text-gray-900">{generatedContent.subject}</p>
                      </div>
                    )}

                    {/* Generated Email */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900">Generated Email</h3>
                        <button
                          onClick={() => copyToClipboard(generatedContent.content || generatedContent.reply, 'content')}
                          className={`text-sm flex items-center transition-all ${
                            copiedField === 'content'
                              ? 'text-green-600'
                              : 'text-primary-600 hover:text-primary-700'
                          }`}
                        >
                          {copiedField === 'content' ? (
                            <>
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Copied!
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              Copy
                            </>
                          )}
                        </button>
                      </div>
                      <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed">
                        <ReactMarkdown>
                          {generatedContent.content || generatedContent.reply}
                        </ReactMarkdown>
                      </div>
                    </div>

                    {/* Statistics */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
                      <h3 className="text-sm font-semibold text-blue-900 mb-4">📊 Statistics</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-blue-600 mb-1">Word Count</div>
                          <div className="text-lg font-semibold text-blue-900">
                            {(generatedContent.content || generatedContent.reply || '').split(/\s+/).filter(Boolean).length}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-blue-600 mb-1">Characters</div>
                          <div className="text-lg font-semibold text-blue-900">
                            {(generatedContent.content || generatedContent.reply || '').length}
                          </div>
                        </div>
                        {generatedContent.subject && (
                          <div>
                            <div className="text-xs text-blue-600 mb-1">Subject Length</div>
                            <div className="text-lg font-semibold text-blue-900">
                              {generatedContent.subject.length} chars
                            </div>
                          </div>
                        )}
                        <div>
                          <div className="text-xs text-blue-600 mb-1">Reading Time</div>
                          <div className="text-lg font-semibold text-blue-900">
                            {Math.ceil((generatedContent.content || generatedContent.reply || '').split(/\s+/).filter(Boolean).length / 200)} min
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tips */}
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 border border-green-100">
                      <h3 className="text-sm font-semibold text-green-900 mb-3">💡 Tips</h3>
                      <ul className="space-y-2 text-xs text-green-800">
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Review and personalize the content before sending</span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Check for any specific details that need adjustment</span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Add your personal touch to make it more authentic</span>
                        </li>
                      </ul>
                    </div>
                  </>
                ) : (
                  <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                    <div className="text-6xl mb-4">📧</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {mode === 'beautify' ? 'Ready to Beautify' : 'Ready to Reply'}
                    </h3>
                    <p className="text-gray-600">
                      {mode === 'beautify'
                        ? 'Enter your email content and click "Beautify Email" to get started'
                        : 'Paste the email you received and click "Generate Reply" to get started'
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Prompt Modal */}
      <UpgradePrompt
        show={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        type={upgradePromptData.type}
        featureName={upgradePromptData.featureName}
        currentPlan={upgradePromptData.currentPlan}
        requiredPlan={upgradePromptData.requiredPlan}
      />
      </div>
    </>
  );
};

export default EmailMarketing;
